import { SignUpModal } from '../../../components/shared';
import React, { useContext, useState } from 'react';
import { GlobalSiteDataContext } from '../../../context';
import { DynamicHeroContent, DynamicHeroCta } from './DynamicHeroContent';
import { hasRichTextContent, hasText } from '../../../utils/general/builder';
import './HeroSections.styles.css';

const styles = {
    backgrounds: {
        section: (color) => ({ backgroundColor: color }),
        content: (color) => ({ backgroundColor: color }),
    },
};

const commonClasses = {
    imageContainer: 'position-relative dynamic-hero-image-wrap',
    contentWrapper: 'd-flex flex-column justify-content-center h-100',
};

export function DynamicHeroBuilderV2({
    heroDesktopImage,
    heroTabletImage,
    heroMobileImage,
    heroMobileImageAlt,
    typefaceLogo,
    heading,
    headingColor,
    headingColorMobileAndTablet,
    disclaimerColor,
    disclaimerColorMobileAndTablet,
    disclaimerLinkText,
    disclaimerTextPromise,
    buttonLink,
    buttonLabel,
    richTextContentMobileAndTablet,
    richTextContentDesktop,
    isHeroImgOnRight,
    footerText,
    logoImageAlt,
    desktopImageBackgroundColor,
    desktopBackgroundColor,
    contentBackgroundColor,
    footerTextColor,
    footerTextColorMobileAndTablet,
}) {
    const siteData = useContext(GlobalSiteDataContext);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const hasCta = Boolean(buttonLink && buttonLabel);
    // Heroes that put all their copy in the promotion graphic have nothing left for the
    // mobile content column, and an empty column still paints a band of color under the CTA
    const hasMobileContent =
        hasText(heading) ||
        hasText(disclaimerLinkText) ||
        hasText(disclaimerTextPromise) ||
        hasText(footerText) ||
        hasRichTextContent(richTextContentMobileAndTablet);

    return (
        // Main container with desktop background color
        <section
            className="position-relative overflow-hidden"
            style={styles.backgrounds.section(desktopImageBackgroundColor)}
        >
            {/* Modal Component */}
            {showSignUpModal && (
                <SignUpModal
                    siteData={siteData}
                    showSignUpModal={showSignUpModal}
                    setShowSignUpModal={setShowSignUpModal}
                />
            )}

            {/* Main content container using Bootstrap grid */}
            <div className="container-fluid px-0">
                <div className={`row ${isHeroImgOnRight ? 'flex-row-reverse' : ''}`}>
                    {/* Image Column */}
                    <div className="col-12 col-lg-6 px-0">
                        <div className={commonClasses.imageContainer}>
                            {/* Mobile Image */}
                            <picture>
                                <source media="(min-width: 992px)" srcSet={heroDesktopImage} />
                                <source media="(min-width: 600px)" srcSet={heroTabletImage} />
                                <img src={heroMobileImage} alt={heroMobileImageAlt} className="img-fluid" />
                            </picture>
                        </div>

                        {/* Mobile/Tablet CTA sits directly under the graphic rather than in the
                            content column, painted with the same mobile background as the content
                            below it so there is no color seam under the button */}
                        {hasCta && (
                            <div
                                className="d-lg-none text-center dynamic-hero-mobile-cta"
                                style={styles.backgrounds.content(contentBackgroundColor)}
                            >
                                <DynamicHeroCta {...{ buttonLink, buttonLabel }} />
                            </div>
                        )}
                    </div>

                    {/* Content Column */}
                    <div className="col-12 col-lg-6" style={styles.backgrounds.content(desktopBackgroundColor)}>
                        {/* Desktop Content with right padding */}
                        <div className="d-none d-lg-block h-100">
                            <div className="container h-100">
                                <div className="row h-100 pt-xxl-5">
                                    <div className="col-lg-12 col-xxl-9 col-xxxl-9 h-100 pe-xxl-5 milan-py-lg">
                                        {' '}
                                        {/* Using col-11 to create right padding */}
                                        <div className={`${commonClasses.contentWrapper} py-5`}>
                                            <DynamicHeroContent
                                                {...{
                                                    buttonLabel,
                                                    buttonLink,
                                                    heading,
                                                    headingColor,
                                                    disclaimerLinkText,
                                                    disclaimerTextPromise,
                                                    disclaimerColor,
                                                    logoImageAlt,
                                                    richTextContent: richTextContentDesktop,
                                                    setShowSignUpModal,
                                                    typefaceLogo,
                                                    footerText,
                                                    footerTextColor,
                                                    showButton: hasCta,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile/Tablet Content */}
                        {hasMobileContent && (
                            <div
                                className="d-block d-lg-none"
                                style={styles.backgrounds.content(contentBackgroundColor)}
                            >
                                <div className="container dynamic-hero-mobile-content">
                                    <DynamicHeroContent
                                        {...{
                                            buttonLabel,
                                            buttonLink,
                                            heading,
                                            headingColor: headingColorMobileAndTablet,
                                            disclaimerLinkText,
                                            disclaimerTextPromise,
                                            disclaimerColor: disclaimerColorMobileAndTablet,
                                            logoImageAlt,
                                            richTextContent: richTextContentMobileAndTablet,
                                            setShowSignUpModal,
                                            typefaceLogo,
                                            footerText,
                                            footerTextColor: footerTextColorMobileAndTablet,
                                            showButton: false,
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
